/**
 * QR Service
 * Frontend service for QR code verification and consumption
 */

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface ProductInfo {
  success: boolean;
  qrType: 'outer' | 'inner';
  product: {
    name: string;
    serialNumber: number;
    manufacturer: string;
    activationStatus: 'CREATED' | 'ACTIVATED' | 'CONSUMED';
    status: string;
    createdAt: string;
    batchId?: string;
    currentOwner?: string;
  };
  scanCount: number;
  supplyChain: Array<{
    from: string;
    to: string;
    date: string;
  }>;
  warnings?: string[];
  message?: string;
  canConsume: boolean;
  consumedAt?: string;
  error?: string;
  enhancedInfo?: EnhancedProductDisplay;
  anomalyDetection?: {
    riskLevel: 'low' | 'medium' | 'high';
    riskScore: number;
    recommendStepUp: boolean;
    uiMessage: AnomalyUIMessage;
  };
  uiGuidance?: AnomalyUIMessage;
}

export interface AnomalyUIMessage {
  type: 'info' | 'warning' | 'alert';
  title: string;
  message: string;
  style: string;
  showStepUp: boolean;
  ctaText?: string;
  emphasis?: boolean;
}

export interface EnhancedProductDisplay {
  productName: string;
  serialNumber: number;
  productType?: 'manufactured' | 'produce';
  batchInfo: {
    batchId: string;
    manufacturerName: string;
    createdAt: Date;
  };
  images: ProductImageDisplay[];
  nutritionInfo?: NutritionInformation;
  customContent?: CustomProductContent;
  produceDetails?: ProduceDetails;
  manufacturingDetails?: ManufacturingDetails;
}

export interface ProductImageDisplay {
  id: string;
  url: string;
  thumbnailUrl: string;
  altText?: string;
  isPrimary: boolean;
  displayOrder: number;
}

export interface NutritionInformation {
  servingSize: string;
  servingsPerContainer?: number;
  caloriesPerServing: number;
  caloriesPer100g?: number;
  macronutrients: MacronutrientInfo;
  vitamins?: VitaminContent[];
  minerals?: MineralContent[];
}

export interface MacronutrientInfo {
  protein: number;
  carbohydrates: number;
  totalFat: number;
  saturatedFat?: number;
  transFat?: number;
  fiber?: number;
  sugar?: number;
  sodium: number;
}

export interface VitaminContent {
  name: string;
  amount: number;
  unit: string;
  dailyValuePercent?: number;
}

export interface MineralContent {
  name: string;
  amount: number;
  unit: string;
  dailyValuePercent?: number;
}

export interface CustomProductContent {
  ingredients?: string;
  allergens?: string[];
  usageInstructions?: string;
  storageInstructions?: string;
  customMessage?: string;
  brandStory?: string;
}

export interface ManufacturingDetails {
  manufacturingDate?: Date;
  expiryDate?: Date;
  batchNumber?: string;
  facilityLocation?: string;
  mrp?: number;
}

export interface ProduceDetails {
  harvestDate?: Date;
  origin?: string;
  variety?: string;
  storageInstructions?: string;
  expiryDate?: Date;
  bestBeforeDays?: number;
}

export interface ConsumerData {
  email?: string;
  phone?: string;
  name?: string;
  purchaseLocation?: string;
  rating?: number;
  feedback?: string;
}

export interface ConsumptionResult {
  success: boolean;
  message: string;
  consumedAt?: string;
  error?: string;
}

class QRService {
  /**
   * Verify QR code and get product information
   */
  async verifyQR(qrToken: string, location?: { latitude: number; longitude: number }): Promise<ProductInfo> {
    try {
      const response = await axios.post(`${API_URL}/qr/verify`, {
        qrToken,
        latitude: location?.latitude,
        longitude: location?.longitude,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to verify QR code');
    }
  }

  /**
   * Mark product as consumed
   */
  async markAsConsumed(
    qrToken: string,
    consumerData?: ConsumerData,
    location?: { latitude: number; longitude: number }
  ): Promise<ConsumptionResult> {
    try {
      const response = await axios.post(`${API_URL}/qr/consume`, {
        qrToken,
        consumerData,
        latitude: location?.latitude,
        longitude: location?.longitude,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to mark product as consumed');
    }
  }

  /**
   * Get browser geolocation (requires user permission)
   */
  async getBrowserLocation(): Promise<{ latitude: number; longitude: number } | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
          // User denied or error occurred
          resolve(null);
        },
        {
          timeout: 5000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }
}

export const qrService = new QRService();
